import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDownMultiple from "@components/ui/dropdown/DropDownMultiple";
import { useState } from "react";
import { emailTemplatesApi } from "@services/api";
import { libraryDocumentsApi } from "@services/api/libraryDocuments";
import Dropzone from "@components/ui/blocks/Dropzone";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import { EmailTemplate } from "types/email_template";
import { Timestamp } from "firebase/firestore";

import { COMPANY_TYPE_OPTIONS as ALL_COMPANY_TYPE_OPTIONS, HIDDEN_COMPANY_TYPES } from "@constants/partnerTypes";

const COMPANY_TYPE_OPTIONS = [
  ...ALL_COMPANY_TYPE_OPTIONS,
  { value: "assurance_do", label: "Assurance DO" },
  { value: "bureau_controle", label: "Bureau de controle" },
  { value: "bureau_etude_beton", label: "Bureau etude beton" },
  { value: "etude_filiere", label: "Etude de filiere" },
  { value: "huissier", label: "Huissier" },
  { value: "sans_decennale", label: "Sans décennale" },
].filter((o) => !HIDDEN_COMPANY_TYPES.has(o.value));

export const EmailTemplateModal = ({
  onClose,
  templateId,
}: {
  onClose: () => void;
  templateId: string;
}) => {
  const emailTemplates = useProjectStore((state) => state.emailTemplates);
  const updateEmailTemplate = useProjectStore(
    (state) => state.updateEmailTemplate,
  );
  const removeEmailTemplates = useProjectStore(
    (state) => state.removeEmailTemplates,
  );
  const libraryDocuments = useProjectStore((state) => state.libraryDocuments);
  const addLibraryDocument = useProjectStore((state) => state.addLibraryDocument);
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);
  const [isUploading, setIsUploading] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);

  const isExisting = !!templateId;
  const blankTemplate: EmailTemplate = {
    id: "",
    name: "",
    destination_type: "client",
    subject: "",
    body: "",
    library_documents_id: [],
    created_at: Timestamp.now(),
  };
  const [template, setTemplate] = useState<EmailTemplate>(
    emailTemplates.find((t) => t.id === templateId) ?? blankTemplate,
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSave = async () => {
    if (!template.name.trim()) {
      showNotification({ type: "error", message: "Le nom est obligatoire" });
      return;
    }
    if (isExisting) {
      updateEmailTemplate(template);
      showNotification({ type: "success", message: "Modèle enregistré" });
      onClose();
    } else {
      try {
        const result = await emailTemplatesApi.create({
          ...template,
          org_id: userData?.org_id || "",
        } as any);
        useProjectStore.getState().setEmailTemplates([
          ...useProjectStore.getState().emailTemplates,
          result,
        ]);
        showNotification({ type: "success", message: "Modèle créé avec succès" });
        onClose();
      } catch {
        showNotification({ type: "error", message: "Erreur lors de la création du modèle" });
      }
    }
  };

  const handleDelete = () => {
    if (!template) return;
    removeEmailTemplates([template.id]);
    showNotification({
      type: "success",
      message: "Modèle supprimé",
    });
    onClose();
  };

  const handleFileDrop = async (files: File[]) => {
    if (!orgData?.id || files.length === 0) return;
    setDroppedFiles(files);
    setIsUploading(true);
    try {
      const newIds: string[] = [];
      for (const file of files) {
        const doc = await libraryDocumentsApi.upload(file, orgData.id);
        addLibraryDocument(doc, orgData.id);
        newIds.push(doc.id);
      }
      setTemplate((prev) => ({
        ...prev,
        library_documents_id: [...(prev.library_documents_id || []), ...newIds],
      }));
      showNotification({
        type: "success",
        message: `${newIds.length} document${newIds.length > 1 ? "s" : ""} ajouté${newIds.length > 1 ? "s" : ""} à la bibliothèque`,
      });
      setDroppedFiles([]);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'upload du document" });
      setDroppedFiles([]);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDuplicate = async () => {
    if (!template) return;
    try {
      const { id, ...rest } = template;
      const result = await emailTemplatesApi.create({
        ...rest,
        name: "Copie de " + template.name,
        org_id: userData?.org_id || "",
      } as any);
      useProjectStore.getState().setEmailTemplates([
        ...useProjectStore.getState().emailTemplates,
        result,
      ]);
      showNotification({
        type: "success",
        message: "Modèle dupliqué avec succès",
      });
      onClose();
    } catch {
      showNotification({
        type: "error",
        message: "Erreur lors de la duplication du modèle",
      });
    }
  };

  return (
    <>
    {showDeleteConfirm && (
      <ConfirmationModal
        title="Supprimer ce modèle d'email ?"
        message={`Le modèle "${template?.name}" sera supprimé définitivement.`}
        confirmText="CONFIRMER"
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
      />
    )}
    <BaseModal
      title="Modèle d'email"
      startIcon={<Icon1 name="email" size={20}></Icon1>}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        <TextField
          label="Nom"
          placeholder="Modèle pour pièces complémentaires"
          value={template?.name}
          onChange={(value) =>
            setTemplate((prev) =>
              prev ? { ...prev, name: value } : prev,
            )
          }
        />
        <AreaTextField
          rows={1}
          label="Sujet"
          placeholder="Demande de pièces complémentaires"
          value={template?.subject}
          onChange={(value) =>
            setTemplate((prev) =>
              prev ? { ...prev, subject: value } : prev,
            )
          }
        />
        <AreaTextField
          rows={12}
          label="Contenu"
          placeholder={`Bonjour,

Pouvez-vous nous transmettre l'étude thermique, les plans  ainsi que l'accord de permis de construire du dossier @nom du mandataire @client sur la commune de @ville.

En vous remerciant par avance.

Cordialement,

* Ne pas répondre à ce mail
`}
          value={template?.body}
          onChange={(value) =>
            setTemplate((prev) =>
              prev ? { ...prev, body: value } : prev,
            )
          }
        />
        <div className="p-2 text-h3 text-neutral-400 ">
          @contacts @nom_projet @adresse_projet @signature
        </div>

        <DropDownMultiple
          label="Documents de la bibliothèque"
          placeholder="Sélectionner des documents"
          options={libraryDocuments.map((doc) => ({
            value: doc.id,
            label: doc.name,
          }))}
          value={template?.library_documents_id || []}
          onSelect={(ids) =>
            setTemplate((prev) =>
              prev
                ? { ...prev, library_documents_id: ids as string[] }
                : prev,
            )
          }
        />
        <Dropzone
          onDrop={handleFileDrop}
          files={droppedFiles}
          loading={isUploading}
          title="Ajouter un document à la bibliothèque"
          dropzoneStyles="rounded-xl"
        />

        <SwitchBrick
          title="Photo obligatoire"
          description="L'utilisateur devra joindre une photo pour envoyer cet email"
          checked={template.mandatory_photo ?? false}
          onChange={(checked) =>
            setTemplate((prev) => ({ ...prev, mandatory_photo: checked }))
          }
        />
        <SwitchBrick
          title="Masquer des suggestions"
          description="Ce modèle reste modifiable ici, mais n'apparaîtra pas dans la liste des modèles lors de l'envoi d'un email (web et mobile)"
          checked={template.hidden_from_suggestions ?? false}
          onChange={(checked) =>
            setTemplate((prev) => ({ ...prev, hidden_from_suggestions: checked }))
          }
        />
        <SwitchBrick
          title="Destiné aux artisans"
          description="Ce modèle sera utilisé pour les emails envoyés aux artisans et non aux clients"
          checked={template.destination_type === "partner"}
          onChange={(checked) =>
            setTemplate((prev) => ({
              ...prev,
              destination_type: checked ? "partner" : "client",
              partner_company_types: checked ? prev.partner_company_types : [],
            }))
          }
        />
        {template.destination_type === "partner" && (
          <DropDownMultiple
            label="Types d'artisans"
            placeholder="Tous les artisans"
            options={COMPANY_TYPE_OPTIONS}
            value={(template.partner_company_types || []).filter(
              (t) => !HIDDEN_COMPANY_TYPES.has(t),
            )}
            onSelect={(values) => {
              const preserved = (template.partner_company_types || []).filter(
                (t) => HIDDEN_COMPANY_TYPES.has(t),
              );
              setTemplate((prev) => ({
                ...prev,
                partner_company_types: [...(values as string[]), ...preserved],
              }));
            }}
          />
        )}
      </div>

      <div className="border-t-1 p-4 flex flex-row items-center gap-2">
        {isExisting && (
          <>
            <SecondaryButton
              variant="small"
              danger
              onClick={() => setShowDeleteConfirm(true)}
            >
              Supprimer
            </SecondaryButton>
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="copy" size={20} />}
              onClick={handleDuplicate}
            >
              Dupliquer
            </SecondaryButton>
          </>
        )}
        <div className="ml-auto flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Annuler
          </SecondaryButton>
          <Button variant="small" label="Enregistrer" onClick={handleSave} />
        </div>
      </div>
    </BaseModal>
    </>
  );
};
